import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTotalTasksCount } from '@/hooks/useTotalTasksCount';

// Mini product mockup inside the hero
const HeroProductMock = () => (
  <div
    style={{
      background: '#0F0E17',
      borderRadius: '20px',
      padding: '14px',
      boxShadow: '0 40px 80px rgba(124,58,237,0.25), 0 20px 40px rgba(0,0,0,0.4)',
      transform: 'perspective(1200px) rotateX(2deg)',
      transformOrigin: 'top center',
      width: '100%',
      maxWidth: '600px',
    }}
  >
    {/* Chrome bar */}
    <div className="flex items-center gap-1.5 mb-3">
      <div className="w-3 h-3 rounded-full" style={{ background: '#EF4444' }}></div>
      <div className="w-3 h-3 rounded-full" style={{ background: '#F59E0B' }}></div>
      <div className="w-3 h-3 rounded-full" style={{ background: '#10B981' }}></div>
      <div className="flex-1 mx-3 h-5 rounded-md flex items-center px-2" style={{ background: '#1A1825' }}>
        <span style={{ color: '#64748B', fontSize: '0.65rem', fontFamily: 'Manrope, sans-serif' }}>app.proyectmind.online/dashboard</span>
      </div>
    </div>

    {/* App content */}
    <div className="flex gap-2" style={{ height: '200px' }}>
      {/* Sidebar mini */}
      <div className="flex flex-col gap-1.5 py-2 px-2" style={{ background: '#1A1825', borderRadius: '10px', width: '120px', flexShrink: 0 }}>
        <div className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg" style={{ background: 'rgba(124,58,237,0.2)' }}>
          <div className="w-2.5 h-2.5 rounded-sm" style={{ background: '#7C3AED' }}></div>
          <span style={{ color: '#7C3AED', fontSize: '0.6rem', fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>Dashboard</span>
        </div>
        {['Proyectos', 'Tareas', 'Gantt', 'Kanban', 'IA'].map((item, i) => (
          <div key={item} className="flex items-center gap-1.5 px-2 py-1">
            <div className="w-2 h-2 rounded-sm opacity-50" style={{ background: '#475569' }}></div>
            <span style={{ color: '#64748B', fontSize: '0.58rem', fontFamily: 'Manrope, sans-serif' }}>{item}</span>
          </div>
        ))}
      </div>

      {/* Main area: Kanban preview */}
      <div className="flex-1 flex flex-col gap-1.5">
        {/* Stats row */}
        <div className="grid grid-cols-3 gap-1.5">
          {[
            { label: 'Proyectos', value: '12', color: '#7C3AED' },
            { label: 'Tareas hoy', value: '8', color: '#10B981' },
            { label: 'En riesgo', value: '3', color: '#EF4444' },
          ].map((s) => (
            <div key={s.label} className="rounded-lg p-2" style={{ background: '#1A1825' }}>
              <div style={{ color: s.color, fontSize: '0.9rem', fontWeight: 800, fontFamily: 'Plus Jakarta Sans, sans-serif', lineHeight: 1 }}>{s.value}</div>
              <div style={{ color: '#64748B', fontSize: '0.5rem', fontFamily: 'Manrope, sans-serif', marginTop: 2 }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Kanban columns */}
        <div className="flex gap-1.5 flex-1">
          {[
            { col: 'Por hacer', color: '#475569', tasks: ['Diseño UX', 'Revisión'], dot: '#64748B' },
            { col: 'En progreso', color: '#7C3AED', tasks: ['Dev API', 'Testing'], dot: '#7C3AED' },
            { col: 'Completado', color: '#10B981', tasks: ['Deploy v1', 'Docs'], dot: '#10B981' },
          ].map((col) => (
            <div key={col.col} className="flex-1 rounded-lg p-1.5" style={{ background: '#1A1825' }}>
              <div className="flex items-center gap-1 mb-1.5">
                <div className="w-1.5 h-1.5 rounded-full" style={{ background: col.dot }}></div>
                <span style={{ color: col.color, fontSize: '0.5rem', fontWeight: 700, fontFamily: 'Manrope, sans-serif' }}>{col.col}</span>
              </div>
              {col.tasks.map((task) => (
                <div key={task} className="rounded-md p-1 mb-1" style={{ background: '#0F0E17', border: '1px solid #1E1B30' }}>
                  <div style={{ color: '#CBD5E1', fontSize: '0.5rem', fontFamily: 'Manrope, sans-serif' }}>{task}</div>
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>
    </div>
  </div>
);

const Hero = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t, language } = useLanguage();
  const tasksCount = useTotalTasksCount();

  const trustBadges = [
    '✓ Sin tarjeta de crédito',
    '✓ Setup en 5 minutos',
    tasksCount
      ? `✓ +${tasksCount.toLocaleString()} tareas gestionadas`
      : '✓ +12,000 equipos',
  ];

  return (
    <section
      className="relative overflow-hidden"
      style={{ background: '#FFFCF7', paddingTop: '80px', paddingBottom: '100px' }}
    >
      {/* Background orbs */}
      <div
        className="pm-orb-purple"
        style={{ width: '500px', height: '500px', top: '-100px', right: '-100px', zIndex: 0 }}
      />
      <div
        className="pm-orb-pink"
        style={{ width: '400px', height: '400px', bottom: '-60px', left: '-80px', zIndex: 0 }}
      />
      <div
        className="pm-orb-amber"
        style={{ width: '200px', height: '200px', top: '40%', left: '15%', zIndex: 0 }}
      />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" style={{ zIndex: 1 }}>
        {/* Badge */}
        <div className="flex justify-center mb-6">
          <div
            className="pm-pill"
            style={{
              background: 'rgba(124,58,237,0.08)',
              color: '#7C3AED',
              border: '1px solid rgba(124,58,237,0.2)',
              fontSize: '0.78rem',
              padding: '6px 16px',
            }}
          >
            <span
              className="mr-1.5 font-black"
              style={{
                background: 'linear-gradient(135deg, #7C3AED, #EC4899)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
              }}
            >
              NUEVO
            </span>
            IA predictiva incluida desde el plan gratuito ✨
          </div>
        </div>

        {/* H1 */}
        <h1
          className="text-center mb-6"
          style={{
            fontFamily: "'Plus Jakarta Sans', sans-serif",
            fontSize: 'clamp(2.8rem, 6vw, 5.2rem)',
            fontWeight: 800,
            letterSpacing: '-0.04em',
            lineHeight: 1.08,
            color: '#0F0E17',
          }}
        >
          La forma{' '}
          <em
            className="pm-gradient-text"
            style={{ fontStyle: 'italic', fontWeight: 800 }}
          >
            más inteligente
          </em>
          <br />
          de gestionar proyectos
        </h1>

        {/* Subtitle */}
        <p
          className="text-center mx-auto mb-8"
          style={{
            fontSize: '1.2rem',
            color: '#475569',
            maxWidth: '640px',
            lineHeight: 1.6,
            fontFamily: 'Manrope, sans-serif',
          }}
        >
          {language === 'en'
            ? 'Gantt, Kanban, AI Predictions, OKRs and real-time collaboration in one platform. Free forever.'
            : 'Gantt, Kanban, IA Predictiva, OKRs y colaboración en tiempo real en una sola plataforma. Gratis para siempre.'}
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
          <button
            onClick={() => navigate(user ? '/dashboard' : '/auth')}
            className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-2xl font-bold text-white transition-all"
            style={{
              background: '#7C3AED',
              fontSize: '1rem',
              boxShadow: '0 8px 24px rgba(124,58,237,0.40)',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            {user ? 'Ir al Dashboard' : 'Comenzar gratis'} →
          </button>
        </div>

        {/* Trust badges */}
        <div className="flex flex-wrap items-center justify-center gap-4 mb-14">
          {trustBadges.map((badge) => (
            <span
              key={badge}
              style={{
                color: '#64748B',
                fontSize: '0.85rem',
                fontFamily: 'Manrope, sans-serif',
                fontWeight: 500,
              }}
            >
              {badge}
            </span>
          ))}
        </div>

        {/* Product mockup with floating badges */}
        <div className="relative flex justify-center">
          {/* Floating badge left */}
          <div
            className="pm-float-neg hidden lg:flex items-center gap-2 absolute -left-4 top-12 z-10"
            style={{
              background: 'white',
              border: '1.5px solid #E2E8F0',
              borderRadius: '14px',
              padding: '10px 14px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
              minWidth: '170px',
            }}
          >
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: '#DCFCE7' }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8l3.5 3.5L13 4.5" stroke="#10B981" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
            <div>
              <div style={{ color: '#64748B', fontSize: '0.6rem', fontFamily: 'Manrope, sans-serif', fontWeight: 600 }}>Hoy completaste</div>
              <div style={{ color: '#0F0E17', fontSize: '0.78rem', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800 }}>8 tareas (+30%)</div>
            </div>
          </div>

          {/* Floating badge right */}
          <div
            className="pm-float hidden lg:flex items-center gap-2 absolute -right-4 top-8 z-10"
            style={{
              background: 'white',
              border: '1.5px solid #E2E8F0',
              borderRadius: '14px',
              padding: '10px 14px',
              boxShadow: '0 8px 24px rgba(0,0,0,0.10)',
              minWidth: '170px',
            }}
          >
            <div
              className="w-8 h-8 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: '#F5F3FF' }}
            >
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M8 1L9.5 5.5H14.5L10.5 8.5L12 13L8 10L4 13L5.5 8.5L1.5 5.5H6.5L8 1Z" fill="#7C3AED" opacity="0.8"/>
              </svg>
            </div>
            <div>
              <div style={{ color: '#64748B', fontSize: '0.6rem', fontFamily: 'Manrope, sans-serif', fontWeight: 600 }}>IA detectó</div>
              <div style={{ color: '#0F0E17', fontSize: '0.78rem', fontFamily: 'Plus Jakarta Sans, sans-serif', fontWeight: 800 }}>3 tareas en riesgo</div>
            </div>
          </div>

          <HeroProductMock />
        </div>
      </div>
    </section>
  );
};

export default Hero;
